import { CourseSection, Task } from '@/shared/model/academy-course-content.model'
import { CreateAcademyCourseDto, UpdateAcademyCourseDto } from '@/shared/model/academy-course.model'
import axios from 'axios'
import { createResource } from '../utils/resource'

const baseStore = createResource('academy/courses', {})

const academyCourseStore = {
  ...baseStore,
  actions: {
    ...baseStore.actions,
    async getAcademyCourse({}, { id }: { id: string }) {
      try {
        const result = await axios.get('/api/academy/courses/' + id)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async getCourseContent({}, { id }: { id: string }) {
      try {
        const result = await axios.get('/api/academy/courses/content/' + id)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async createAcademyCourse({}, { item }: { item: CreateAcademyCourseDto }) {
      try {
        const result = await axios.post('/api/academy/courses', item)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async updateAcademyCourse({}, { item, id }: { item: UpdateAcademyCourseDto; id: string }) {
      try {
        const result = await axios.put(`/api/academy/courses/${id}`, item)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async addCourseContent({}, { item, id }: { item: { name: string; language: string }; id: string }) {
      try {
        const result = await axios.post(`/api/academy/courses/${id}/content`, item)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async updateCourseContent({}, { item, id }: { item: any; id: string }) {
      try {
        const result = await axios.put(`/api/academy/courses/content/${id}`, item)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async addCourseContentSection({}, { courseId, data }: { data: CourseSection; courseId: string }) {
      try {
        const result = await axios.post(`/api/academy/courses/content/${courseId}/sections`, data)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async removeCourseContent({}, { courseId }: { courseId: string }) {
      try {
        const result = await axios.delete(`/api/academy/courses/content/${courseId}`)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async removeCourseContentSection({}, { sectionId, courseId }: { sectionId: string; courseId: string }) {
      try {
        const result = await axios.delete(`/api/academy/courses/content/${courseId}/sections/${sectionId}`)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async addCourseContentTask({}, { sectionId, courseId, data }: { sectionId: string; courseId: string; data: Task }) {
      try {
        const result = await axios.post(`/api/academy/courses/content/${courseId}/sections/${sectionId}/tasks/`, data)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async removeCourseContentTask(
      {},
      { sectionId, courseId, taskId }: { sectionId: string; courseId: string; taskId: string },
    ) {
      try {
        const result = await axios.delete(
          `/api/academy/courses/content/${courseId}/sections/${sectionId}/tasks/${taskId}`,
        )
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },

    async removeCourseContentAttachment({}, { fileId, courseId }: { fileId: string; courseId: string }) {
      try {
        const result = await axios.delete(`/api/academy/courses/content/${courseId}/attachment/${fileId}`)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async removeCourseContentSectionAttachment(
      {},
      { fileId, courseId, sectionId }: { fileId: string; courseId: string; sectionId: string },
    ) {
      try {
        const result = await axios.delete(
          `/api/academy/courses/content/${courseId}/sections/${sectionId}/attachments/${fileId}`,
        )
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async deleteAcademyCourse({}, { id }: { id: string }) {
      try {
        const result = await axios.delete(`/api/academy/courses/${id}`)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async publishAcademyCourse(context: any, { id, published }: { id: string; published: boolean }) {
      try {
        const result = await axios.put(`/api/academy/courses/${id}/publish`, { published })
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async getPublishedCourses(
      context: any,
      {
        page = 1,
        limit = 12,
        search = '',
        level,
      }: { page?: number; limit?: number; search?: string; level?: string } = {},
    ) {
      try {
        const params: any = { page: page - 1, limit, search }
        if (level) params.level = level

        const result = await axios.get('/api/academy/courses/published', { params })
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async getCourseDetails(context: any, { id }: { id: string }) {
      try {
        const result = await axios.get(`/api/academy/courses/${id}/details`)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async purchaseCourse(context: any, { courseId }: { courseId: string }) {
      try {
        const result = await axios.post(`/api/academy/courses/${courseId}/purchase`)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
    async copyAcademyCourse(context: any, { item, id }: { item: { name: string }; id: string }) {
      try {
        const result = await axios.post(`/api/academy/courses/${id}/clone`, item)
        return { data: result.data, error: null }
      } catch (err) {
        return { data: null, error: err }
      }
    },
  },
}

export default academyCourseStore
